import * as React from "react";

import Tile from "./elements/Tile";
import Top from "./elements/Top";
import Bottom from "./elements/Bottom";
import { Title, Subtitle, Meta } from "./elements/Typo";
import Overlay from "./elements/Overlay";
import Icon from "../../elements/Icon";
import Text from "../../elements/Text";

import {
  ActivityType,
  RegistrationStatus,
  ActivityTypeDisplayMap
} from "./types";
import ActivityTypeTag from "./elements/ActivityTypeTag";
import { format } from "date-fns";

type Props = {
  style?: React.CSSProperties;
  className?: string;
  imageSrc: string;
  title: string;
  supplier: string;
  progress: number;
  status: RegistrationStatus | null;
  type: ActivityType;
  typeMap: ActivityTypeDisplayMap;
  onClick?: () => void;
  disabled?: boolean;
  texts: {
    disabledMessage: string;
    notStarted: string;
    finished: string;
    studyTime: string;
    examDate: string;
    examDateFinished: string;
    examDateEmpty: string;
    examPassed: string;
    examFailed: string;
    hoverButton?: string;
  };
  studyTime?: number | null;
  examData?: {
    baseUrl: string;
    examDate: string;
    result: RegistrationStatus;
    type: "EXAM";
  } | null;
};

function UserActivityTile(
  {
    style,
    className,
    imageSrc,
    title,
    supplier,
    progress,
    status,
    type,
    typeMap,
    onClick,
    disabled,
    texts,
    studyTime,
    examData
  }: Props,
  ref: any
) {
  let contentBottomLeft = null;
  if (type === "EXAM") {
    if (examData && !examData.examDate) {
      contentBottomLeft = <Meta>{texts.examDateEmpty}</Meta>;
    } else if (examData && examData.examDate) {
      contentBottomLeft = (
        <Meta>
          <div style={{ marginBottom: 2 }}>
            {examData.result === "PASSED" || examData.result === "FAILED"
              ? texts.examDateFinished
              : texts.examDate}
            :
          </div>
          <div style={{ color: "#3e3e3e" }}>
            {format(examData.examDate, "DD-MM-YYYY")}
          </div>
        </Meta>
      );
    }
  } else if (status === "COMPLETED") {
    contentBottomLeft = (
      <>
        <Icon
          name="finished"
          c={["success", 5]}
          size={14}
          style={{ marginRight: 4 }}
        />
        <Meta>{texts.finished}</Meta>
      </>
    );
  } else if (!progress) {
    contentBottomLeft = <Meta>{texts.notStarted}</Meta>;
  } else {
    contentBottomLeft = <Meta>{Math.round(progress)}%</Meta>;
  }

  let contentBottomRight = null;
  if (type === "EXAM" && examData && examData.result) {
    if (examData.result === "PASSED") {
      contentBottomRight = (
        <>
          <Icon
            name="finished"
            c={["success", 5]}
            size={14}
            style={{ marginRight: 4 }}
          />
          <Meta>{texts.examPassed}</Meta>
        </>
      );
    }
    if (examData.result === "FAILED") {
      contentBottomRight = (
        <>
          <Icon
            name="close"
            c={["error", 5]}
            size={14}
            style={{ marginRight: 4 }}
          />
          <Meta>{texts.examFailed}</Meta>
        </>
      );
    }
  } else if (studyTime) {
    contentBottomRight = (
      <Meta>
        {studyTime} {texts.studyTime}
      </Meta>
    );
  }

  const [isOver, setOver] = React.useState(false);

  return (
    <Tile
      ref={ref}
      style={style}
      className={className}
      onClick={onClick}
      disabled={disabled}
      overlay={
        <Overlay>
          <Text
            fw={"bold"}
            style={{ maxWidth: "80%", textAlign: "center" }}
            c="white"
          >
            {texts.disabledMessage}
          </Text>
        </Overlay>
      }
      showOverlayOnHover={disabled}
      progress={progress || 0}
      showProgressBar={type !== "EXAM"}
      onMouseOverChange={setOver}
    >
      <Top imageSrc={imageSrc}>
        <ActivityTypeTag {...typeMap[type]} />
      </Top>
      <Bottom
        topLeft={
          <>
            <Title>{title}</Title>
            <Subtitle>{supplier}</Subtitle>
          </>
        }
        isOver={isOver}
        bottomLeft={contentBottomLeft}
        HoverButtonText={status !== "COMPLETED" && texts.hoverButton}
        bottomRight={contentBottomRight}
      />
    </Tile>
  );
}

export default React.memo(React.forwardRef(UserActivityTile), (prev, next) => {
  if (prev.title !== next.title) {
    return false;
  }

  if (prev.supplier !== next.supplier) {
    return false;
  }

  if (prev.imageSrc !== next.imageSrc) {
    return false;
  }

  if (prev.progress !== next.progress) {
    return false;
  }

  if (prev.status !== next.status) {
    return false;
  }

  return true;
});
